import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { INDUSTRIES } from "@/content/industries";
import { siteConfig } from "@/config/site";


const INDUSTRY_FAQS = [
  {
    q: "Why does vertical specialisation matter for SEO rather than just running a generic playbook?",
    a: "Because search behaviour, competitive density, and conversion paths differ enormously by vertical. An HVAC company competes on same-day availability and Local Pack visibility for emergency queries; a B2B SaaS company competes on comparison and integration content read by a buying committee over weeks. A generic technical-SEO checklist applies everywhere, but the content strategy, keyword prioritisation, and even the schema markup that matters most change by vertical — treating them the same wastes budget on the wrong priorities.",
  },
  {
    q: "How does the buyer journey actually differ between, say, automotive and legal?",
    a: `Automotive buyers often search with high commercial intent close to a decision — "used Honda Civic under 15k ${siteConfig.contact.address.city}" — and convert on inventory pages within a single session. Legal buyers, particularly in personal injury or family law, search under stress, compare multiple firms over days, and weigh trust signals like reviews and attorney credentials more heavily than price. A programme built for automotive's inventory-driven urgency would under-serve legal's longer, trust-based evaluation, and vice versa.`,
  },
  {
    q: "What are YMYL considerations and why do they change the SEO approach?",
    a: "YMYL — Your Money or Your Life — is Google's shorthand for topics that can affect a person's health, finances, safety, or legal standing. Healthcare, legal, and financial verticals fall squarely inside it, which means Google applies materially higher scrutiny to author expertise, site trustworthiness, and factual accuracy before ranking content. For these clients, we invest more heavily in author bios, credential display, citations to primary sources, and structured data that signals expertise — work that a lower-stakes vertical like home services simply doesn't need to the same degree.",
  },
  {
    q: "Does industry specialisation mean you won't work outside your listed verticals?",
    a: "No — it means we're explicit about where we have a repeatable framework versus where we'd be building one from scratch. We take on adjacent verticals when the underlying buyer-journey and competitive dynamics are close enough to what we've already mapped, and we say so plainly during scoping rather than quietly applying a mismatched framework and hoping it holds.",
  },
  {
    q: "How do I pick the right vertical page if my business spans more than one?",
    a: "Start with whichever line of business drives the majority of qualified leads today, since that's where a mismatched strategy costs you the most. If your business genuinely spans two YMYL-adjacent categories — for example, a med spa that's part healthcare, part cosmetic retail — talk to us directly rather than picking a page; the framework usually needs blending rather than a single off-the-shelf fit.",
  },
  {
    q: "Do industry-specific keywords actually convert better than generic ones?",
    a: `Generally yes, because vertical-specific long-tail queries carry more implicit intent. "emergency AC repair Round Rock" converts at a different rate than "AC repair," and a programme tuned to the vertical will prioritise the former even though it has lower search volume, because volume without buyer intent doesn't move revenue.`,
  },
  {
    q: "How often do you update a vertical framework as an industry changes?",
    a: "Continuously, but formally at least twice a year per vertical. Search behaviour shifts — SaaS buyers increasingly research through AI answer engines before ever visiting a comparison site, for instance — and a framework built two years ago without accounting for that shift will under-perform even if the fundamentals were sound when written.",
  },
];


export const Route = createFileRoute("/industries/")({
  head: () => ({ ...pageMeta({
    path: "/industries/",
    title: `Industry SEO Programmes in ${siteConfig.contact.address.city} — Automotive, HVAC & SaaS`,
    description:
      "Vertical SEO programmes engineered for how your buyers actually search: automotive, HVAC, security, SaaS, e-commerce, legal, healthcare, real estate, and professional services. Sourced proof from live engagements.",
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Industries", path: "/industries/" }]),
    faqLd(INDUSTRY_FAQS),
  ) }),
  component: IndustriesIndex,
});

function IndustriesIndex() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-20 md:pt-28 pb-10">
          <EyebrowLabel>Industries · 9 verticals</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[1.02] max-w-4xl text-balance">
            Vertical playbooks.
            <span className="text-gradient"> Built for how your buyer searches.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Generic SEO agencies run the same playbook everywhere. We don't. Each
            vertical has its own buyer journey, competitive traps, and KPI set —
            and its own programme.
          </p>
        </section>

        <section className="container-page pb-24">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((i) => (
              <Link
                key={i.slug}
                to="/industries/$slug/"
                params={{ slug: i.slug }}
                className="surface-card p-6 md:p-7 hover:-translate-y-0.5 transition-transform flex flex-col group"
              >
                <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                  {i.eyebrow}
                </div>
                <div className="mt-2 font-display text-2xl md:text-3xl group-hover:text-primary transition-colors">{i.name}</div>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3 flex-1">
                  {i.hero.deck}
                </p>
                <div className="mt-5 pt-4 border-t border-border text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                  Framework · <span className="text-foreground">{i.framework.name}</span>
                </div>
                <div className="mt-4 text-sm text-primary font-semibold flex items-center gap-1">
                  View Framework <span aria-hidden>→</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="container-page pb-24">
          <div className="surface-card p-8 md:p-10">
            <EyebrowLabel>Not on the list?</EyebrowLabel>
            <h2 className="mt-3 font-display text-3xl md:text-4xl">
              We take on adjacent verticals we can serve honestly.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              If your vertical isn't here, tell us about it. We'd rather turn down
              work than pretend expertise we don't have.
            </p>
            <div className="mt-6">
              <Link
                to="/contact/" search={{ location: "", service: "" }}
                className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
              >
                Start a conversation →
              </Link>
            </div>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>Why vertical strategy</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Why vertical specialisation changes SEO strategy
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              SEO fundamentals — crawlability, page speed, internal linking, sound schema — hold across
              every industry, and we don't reinvent those basics per vertical. What changes dramatically
              is everything downstream of the basics: which query types actually carry commercial intent,
              how competitive the Local Pack or organic results are, what trust signals a buyer needs
              before converting, and which content format (comparison guide, service page, inventory
              listing, reviews) does the heaviest lifting.
            </p>
            <p>
              A vertical-specific framework tells us, before we start, where to spend the first month of
              effort. For a home-services client, that's usually Google Business Profile optimisation and
              service-area pages, because Local Pack visibility drives most of the volume. For a B2B SaaS
              client, the first month is more often technical content and comparison pages targeting
              mid-funnel research queries, because the buying process is longer and less location-bound.
              Applying the home-services playbook to the SaaS client — or the reverse — burns budget
              chasing the wrong signal.
            </p>
            <p>
              Specialisation also means we know where the competitive traps sit in each vertical. In
              e-commerce, thin category pages and duplicate product descriptions are the recurring
              technical debt; in real estate, it's stale MLS-fed listing pages that Google treats as
              low-value; in legal, it's location pages built for every city in a service area without
              enough unique substance to avoid looking templated. Recognising the pattern early, rather
              than discovering it three months into an engagement, is what a vertical framework buys a
              client.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>Buyer journeys</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            How buyer journeys differ by vertical
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Some verticals are decided in minutes; others take weeks. A homeowner with a broken water
              heater searches, calls the first credible-looking result, and converts within the hour —
              which means for that vertical, page speed, prominent phone numbers, and Local Pack ranking
              matter more than long-form educational content. A prospective SaaS buyer, by contrast, might
              read a comparison article, a case study, a pricing page, and a handful of review-site
              listings across several sessions spanning weeks before a demo is even booked, which shifts
              the SEO priority toward middle-of-funnel content that nurtures rather than immediately
              converts.
            </p>
            <p>
              Healthcare and legal sit somewhere in between but lean toward the longer, trust-building
              path — a searcher choosing a personal injury attorney or a specialist physician is rarely
              converting on the first visit, and the content that earns that trust (credentials, patient
              or client outcomes described honestly, clear explanations of process) looks nothing like the
              urgency-driven copy that works for emergency home services.
            </p>
            <p>
              Understanding which pattern your business fits changes what "success" looks like in the
              first ninety days. A home-services client should expect call volume and Local Pack movement
              early; a SaaS client should expect organic sessions and content engagement to move first,
              with pipeline influence showing up later as the funnel matures.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>YMYL</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            YMYL considerations we build around
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Healthcare, legal, and financial-services clients fall under what Google's own guidance
              calls Your Money or Your Life content — pages whose accuracy can materially affect a
              reader's health, finances, or legal standing. Google applies a higher trust bar to this
              content, evaluating not just the page itself but the credentials of the author, the
              reputation of the site, and whether claims are backed by citable sources.
            </p>
            <p>
              In practice, that means our YMYL engagements invest more heavily in visible author
              expertise — named practitioners with credentials, not anonymous "our team" bylines —
              alongside structured data that makes those credentials machine-readable, clear sourcing for
              any factual or statistical claim, and a more conservative approach to speculative or
              trend-chasing content that hasn't been vetted. It's slower and more editorially rigorous
              work than a lower-stakes vertical requires, and clients in these categories should expect
              that trade-off reflected in scope and timeline.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <EyebrowLabel>Choosing your page</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            How to pick your vertical page
          </h2>
          <div className="mt-6 space-y-5 text-[1.05rem] leading-relaxed text-foreground/85">
            <p>
              Choose based on where your revenue actually comes from today, not the label you'd prefer.
              A multi-location retailer with a service arm should generally start from whichever line
              drives more transactions, since that's where a mismatched strategy is most expensive. If
              your business is genuinely hybrid — a healthcare-adjacent product with an e-commerce storefront,
              for example — read both relevant framework pages and expect us to blend elements rather than
              force-fit one.
            </p>
            <p>
              If none of the nine verticals below describe your business precisely, that's a signal to
              talk to us directly rather than guess. We'll tell you plainly whether your business maps
              closely enough to an existing framework to move fast, or whether it needs a bespoke
              strategy built from first principles — which is slower, but more honest than pretending a
              framework fits when it doesn't.
            </p>
          </div>
        </section>

        <section className="container-page pb-16 max-w-3xl border-t border-border pt-16">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Frequently asked questions
          </h2>
          <dl className="mt-8 space-y-6">
            {INDUSTRY_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
